import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { ROUTES } from 'src/constants';
import { Box } from 'src/kit/Box';
import { Flex } from 'src/kit/Flex';

import logo1 from 'images/update_landing/universities/Audencia.png';
import logo2 from 'images/update_landing/universities/CentraleSupélec.png';
import logo3 from 'images/update_landing/universities/Dauphine.png';
import logo4 from 'images/update_landing/universities/EDHEC.jpeg';
import logo5 from 'images/update_landing/universities/emlyon.jpeg';
import logo6 from 'images/update_landing/universities/esade.png';
import logo7 from 'images/update_landing/universities/ESCP.png';
import logo8 from 'images/update_landing/universities/ESSEC.png';
import logo9 from 'images/update_landing/universities/Grenoble.png';
import logo10 from 'images/update_landing/universities/HEC.png';
import logo11 from 'images/update_landing/universities/MINES.png';
import logo12 from 'images/update_landing/universities/Neoma.png';
import logo13 from 'images/update_landing/universities/Paris_Saclay.png';
import logo14 from 'images/update_landing/universities/ParisTech.png';
import logo15 from 'images/update_landing/universities/SciencesPo.png';
import logo16 from 'images/update_landing/universities/Skema.png';
import jobOffersPreview from 'images/new_landing/png/job-offer@3x.png';
import vacancyPreviewImg1 from 'images/new_landing/jpg/get-job-offers-pick-1-mob@3x.jpg';
import vacancyPreviewImg2 from 'images/new_landing/jpg/get-job-offers-pick-2-mob@3x.jpg';

import CompaniesSlider from '../../NewLanding/Employer/CompaniesSlider';
import EmployerLayout from '../../NewLanding/Employer/EmployerLayout';

// Styled Components

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: 180px 5% 140px;
  background: #0d1b2a;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeroGrid = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
`;

const GlowTop = styled.div`
  position: absolute;
  top: -200px;
  right: -200px;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(45, 189, 150, 0.08) 0%, transparent 70%);
  pointer-events: none;
`;

const GlowBottom = styled.div`
  position: absolute;
  bottom: -100px;
  left: 10%;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(201, 168, 76, 0.06) 0%, transparent 70%);
  pointer-events: none;
`;

const HeroTitle = styled.h1`
  position: relative;
  z-index: 2;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.6rem, 5vw, 4.2rem);
  font-weight: 700;
  margin-bottom: 24px;
  letter-spacing: -0.02em;
  color: #ffffff;
  line-height: 1.1;
`;

const HeroSubtitle = styled.p`
  position: relative;
  z-index: 2;
  max-width: 720px;
  font-size: 1.15rem;
  line-height: 1.7;
  color: #8a99a8;
  margin-bottom: 40px;
`;

const PrimaryButton = styled.a`
  position: relative;
  z-index: 2;
  display: inline-block;
  background: #2dbd96;
  color: #0d1b2a;
  padding: 16px 40px;
  border-radius: 4px;
  font-weight: 600;
  text-decoration: none;
  font-size: 1rem;
  transition: background 0.2s, transform 0.15s;

  &:hover {
    background: #1a9b78;
    transform: translateY(-2px);
  }
`;

const SectionWrapper = styled.section<{ bg?: string; textAlign?: string }>`
  padding: 160px 5%;
  background: ${props => props.bg || '#FFFFFF'};
  text-align: ${props => props.textAlign || 'left'};
`;

const ContentFlex = styled(Flex)`
  max-width: 1200px;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 80px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: #0d1b2a;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const SectionDesc = styled.p`
  color: #5b6b7a;
  line-height: 1.7;
  margin-bottom: 40px;
  font-size: 1.05rem;
`;

const StepItem = styled(Flex)`
  margin-bottom: 28px;
  gap: 20px;
  align-items: flex-start;
`;

const StepBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(45, 189, 150, 0.15);
  color: #2dbd96;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StepTitle = styled.div`
  font-weight: 600;
  margin-bottom: 6px;
  color: #0d1b2a;
  font-size: 1.1rem;
`;

const StepDesc = styled.div`
  color: #5b6b7a;
  line-height: 1.6;
`;

const UnivContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 40px 60px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 30px 40px;
  }
`;

const SchoolLogoCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 140px;
  height: 60px;
  transition: transform 0.3s ease;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: grayscale(100%);
    opacity: 0.5;
    transition: all 0.4s ease;
  }

  &:hover {
    transform: translateY(-5px);
  }

  &:hover img {
    filter: grayscale(0%);
    opacity: 1;
    transform: scale(1.1);
  }
`;

const EmployerCTASection = styled.section`
  padding: 120px 5%;
  background: linear-gradient(135deg, #f8fafc 0%, #e8f0f6 100%);
  position: relative;
  overflow: hidden;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const EmployerCTAGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(45, 189, 150, 0.07) 0%, transparent 70%);
  pointer-events: none;
`;

const EmployerCTATitle = styled.h2`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #0d1b2a;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
  line-height: 1.2;
`;

const EmployerCTADesc = styled.p`
  color: #5b6b7a;
  font-size: 1.15rem;
  max-width: 680px;
  margin: 0 auto 40px;
  line-height: 1.7;
  position: relative;
  z-index: 1;
`;

const CandidateOverview = () => {
  return (
    <EmployerLayout>
      <HeroSection>
        <HeroGrid />
        <GlowTop />
        <GlowBottom />

        <HeroTitle>Launch your career in corporate finance.</HeroTitle>

        <HeroSubtitle>
          Access curated roles from PE funds, investment banks and M&A boutiques — with structured assessments and
          transparent timelines.
        </HeroSubtitle>

        <PrimaryButton href={ROUTES.signUpCandidate}>Create your profile</PrimaryButton>
      </HeroSection>

      <SectionWrapper bg="#F4F6F9">
        <ContentFlex>
          <Box style={{ flex: '1 1 520px' }}>
            <SectionTitle>How to join our exclusive community</SectionTitle>

            <SectionDesc>
              A structured, merit-based process designed to identify high-potential candidates for leading corporate
              finance roles.
            </SectionDesc>

            {[
              ['01', 'Create a structured profile', 'Showcase academics, experience and motivations in one place.'],
              ['02', 'Apply to curated roles', 'Access exclusive opportunities across partner firms.'],
              [
                '03',
                'Complete technical assessments',
                'Demonstrate analytical ability through structured evaluations.'
              ],
              ['04', 'Receive ranked feedback', 'Know where you stand within 10 days.']
            ].map(([num, title, desc]) => (
              <StepItem key={num}>
                <StepBadge>{num}</StepBadge>
                <Box>
                  <StepTitle>{title}</StepTitle>
                  <StepDesc>{desc}</StepDesc>
                </Box>
              </StepItem>
            ))}
          </Box>

          <Carousel />
        </ContentFlex>
      </SectionWrapper>
      {/* <SectionWrapper bg="#FFFFFF" textAlign="center">
        <SectionTitle as="h2" style={{ fontSize: '2.4rem' }}>
          Join 60+ partner universities already on Sainoo.
        </SectionTitle>

        <SectionDesc style={{ maxWidth: '600px', margin: '0 auto 50px' }}>
          Top finance students across the UK and Europe are already part of our network.
        </SectionDesc>

        <UnivContainer>
          {[
            { src: logo1, name: 'Audencia' },
            { src: logo2, name: 'CentraleSupélec' },
            { src: logo3, name: 'Dauphine' },
            { src: logo4, name: 'EDHEC' },
            { src: logo5, name: 'emlyon' },
            { src: logo6, name: 'ESADE' },
            { src: logo7, name: 'ESCP' },
            { src: logo8, name: 'ESSEC' },
            { src: logo9, name: 'Grenoble' },
            { src: logo10, name: 'HEC' },
            { src: logo11, name: 'MINES' },
            { src: logo12, name: 'Neoma' },
            { src: logo13, name: 'Paris-Saclay' },
            { src: logo14, name: 'ParisTech' },
            { src: logo15, name: 'Sciences Po' },
            { src: logo16, name: 'Skema' }
          ].map((school, i) => (
            <SchoolLogoCell key={i}>
              <img src={school.src} alt={school.name} />
            </SchoolLogoCell>
          ))}
        </UnivContainer>
        <Box style={{ marginTop: '32px', fontSize: '0.9rem', color: '#8a99a8', fontStyle: 'italic' }}>
          + many more across UK, France, DACH, and Iberia.
        </Box>
      </SectionWrapper> */}

      <SectionWrapper bg="#FFFFFF" textAlign="center" style={{ borderTop: '1px solid #E8ECF0' }}>
        <SectionTitle as="h2" style={{ fontSize: '2.4rem' }}>
          Opportunities sourced directly from leading firms
        </SectionTitle>

        <SectionDesc style={{ maxWidth: '700px', margin: '0 auto 50px' }}>
          Private equity funds, investment banks and elite M&A boutiques trust Sainoo to identify high-potential talent.
        </SectionDesc>

        <CompaniesSlider bg="#FFFFFF" />
      </SectionWrapper>

      <EmployerCTASection>
        <EmployerCTAGlow />
        <EmployerCTATitle>Here to find your next role?</EmployerCTATitle>
        <EmployerCTADesc>
          Join a community of high-potential candidates and get noticed by the world&apos;s most prestigious Corporate
          Finance, PE, and Investment Banking firms.
        </EmployerCTADesc>
        <PrimaryButton href={ROUTES.landingEmployerJobs}>Browse opportunities</PrimaryButton>
      </EmployerCTASection>
    </EmployerLayout>
  );
};

export default CandidateOverview;

const images = [jobOffersPreview, vacancyPreviewImg1, vacancyPreviewImg2];

const CarouselBox = styled(Box)`
  flex: 1 1 420px;
  text-align: center;
`;

const CarouselWrapper = styled(Box)`
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  box-shadow: 0 30px 70px rgba(13, 27, 42, 0.15);
  background: #ffffff;
  padding-bottom: 75%; /* Maintain aspect ratio depending on image size */
`;

const CarouselImage = styled.img<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${props => (props.$active ? 1 : 0)};
  transition: opacity 0.6s ease-in-out;
  pointer-events: ${props => (props.$active ? 'auto' : 'none')};
`;

const DotContainer = styled(Flex)`
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  background: ${props => (props.$active ? '#2DBD96' : '#C1CDD9')};
  transition: background 0.3s ease, transform 0.2s ease;

  &:hover {
    transform: scale(1.2);
    background: ${props => (props.$active ? '#2DBD96' : '#8A99A8')};
  }
`;

const Carousel = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <CarouselBox>
      <CarouselWrapper>
        {images.map((src, i) => (
          <CarouselImage key={i} src={src} alt={'preview-' + i} $active={i === index} />
        ))}
      </CarouselWrapper>

      <DotContainer>
        {images.map((_, i) => (
          <Dot key={i} onClick={() => setIndex(i)} $active={i === index} />
        ))}
      </DotContainer>
    </CarouselBox>
  );
};
